import { useEffect, useState } from "react";
import { Trash2, RotateCcw, X, RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api from "@/lib/api";

function fmtDateTime(iso) {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleString("pt-BR"); } catch { return iso; }
}

function displayNome(doc) {
  return doc.nome || doc.name || doc.email || doc.praticante_nome_snapshot || doc.terapeuta_nome_snapshot || doc.id;
}

export default function Lixeira() {
  const [collections, setCollections] = useState([]);
  const [active, setActive] = useState(null);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [purgeTarget, setPurgeTarget] = useState(null);

  const fetchCollections = async () => {
    try {
      const { data } = await api.get("/lixeira/collections");
      setCollections(data);
      if (!active && data.length) setActive(data[0].collection);
    } catch (e) {
      toast.error("Erro ao carregar lixeira");
    }
  };

  const fetchItems = async (coll) => {
    if (!coll) return;
    setLoading(true);
    try {
      const params = q ? { q } : {};
      const { data } = await api.get(`/lixeira/${coll}`, { params });
      setItems(data);
    } catch (e) {
      toast.error("Erro ao listar itens");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCollections(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { fetchItems(active); /* eslint-disable-next-line */ }, [active]);

  const doRestore = async (item) => {
    try {
      await api.post(`/lixeira/${active}/${item.id}/restore`);
      toast.success(`"${displayNome(item)}" restaurado`);
      await Promise.all([fetchItems(active), fetchCollections()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao restaurar");
    }
  };

  const doPurge = async () => {
    if (!purgeTarget) return;
    try {
      await api.delete(`/lixeira/${active}/${purgeTarget.id}`);
      toast.success("Excluído permanentemente");
      setPurgeTarget(null);
      await Promise.all([fetchItems(active), fetchCollections()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao excluir");
    }
  };

  const totalDeleted = collections.reduce((s, c) => s + (c.count || 0), 0);

  return (
    <div data-testid="lixeira-page">
      <PageHeader
        icon={Trash2}
        title="Lixeira"
        description="Registros excluídos podem ser restaurados ou eliminados permanentemente."
        actions={
          <Button variant="outline" size="sm" onClick={() => { fetchCollections(); fetchItems(active); }} data-testid="lixeira-refresh">
            <RefreshCw className="h-4 w-4 mr-1" /> Atualizar
          </Button>
        }
      />

      <div className="mb-4 flex items-center gap-3 text-sm text-slate-600">
        <Badge variant="secondary" className="text-xs">
          {totalDeleted} registro{totalDeleted !== 1 ? "s" : ""} na lixeira
        </Badge>
      </div>

      <Tabs value={active || ""} onValueChange={setActive}>
        <TabsList className="flex-wrap h-auto justify-start bg-slate-100 p-1 rounded-xl">
          {collections.map((c) => (
            <TabsTrigger key={c.collection} value={c.collection} data-testid={`lixeira-tab-${c.collection}`} className="data-[state=active]:bg-white data-[state=active]:text-brand-turquoise">
              {c.label}
              {c.count > 0 && <span className="ml-1.5 inline-flex items-center rounded-full bg-brand-turquoise text-white text-[10px] px-1.5">{c.count}</span>}
            </TabsTrigger>
          ))}
        </TabsList>

        {collections.map((c) => (
          <TabsContent key={c.collection} value={c.collection} className="mt-4">
            <Card className="border-border shadow-none">
              <CardContent className="p-4">
                <div className="mb-4 flex items-center gap-2 max-w-md">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && fetchItems(active)}
                      placeholder="Buscar por nome/email…"
                      className="pl-9"
                      data-testid="lixeira-search"
                    />
                  </div>
                  <Button size="sm" variant="outline" onClick={() => fetchItems(active)}>Buscar</Button>
                </div>

                {loading ? (
                  <div className="p-12 text-center text-muted-foreground">Carregando…</div>
                ) : items.length === 0 ? (
                  <div className="p-12 text-center text-muted-foreground">Nenhum item excluído nesta coleção.</div>
                ) : (
                  <div className="rounded-lg border border-border overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100 text-slate-500 uppercase text-[10px] tracking-wider">
                        <tr>
                          <th className="text-left px-3 py-2">Identificação</th>
                          <th className="text-left px-3 py-2">Excluído em</th>
                          <th className="text-left px-3 py-2">Excluído por</th>
                          <th className="text-right px-3 py-2 w-56">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((it) => (
                          <tr key={it.id} className="border-t border-border" data-testid={`lixeira-item-${it.id}`}>
                            <td className="px-3 py-2 font-medium text-slate-700">{displayNome(it)}</td>
                            <td className="px-3 py-2 text-slate-500 text-xs">{fmtDateTime(it.deleted_at)}</td>
                            <td className="px-3 py-2 text-slate-500 text-xs">{it.deleted_by_email || "—"}</td>
                            <td className="px-3 py-2 text-right">
                              <div className="inline-flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => doRestore(it)} data-testid={`lixeira-restore-${it.id}`}>
                                  <RotateCcw className="h-3 w-3 mr-1" /> Restaurar
                                </Button>
                                <Button size="sm" variant="destructive" onClick={() => setPurgeTarget(it)} data-testid={`lixeira-purge-${it.id}`}>
                                  <X className="h-3 w-3 mr-1" /> Excluir def.
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      <AlertDialog open={!!purgeTarget} onOpenChange={(o) => !o && setPurgeTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir permanentemente?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação é irreversível. O registro <b>{purgeTarget && displayNome(purgeTarget)}</b> será removido do banco de dados. Um log de auditoria ficará registrado.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doPurge} className="bg-destructive hover:bg-destructive/90" data-testid="lixeira-purge-confirm">
              Excluir definitivamente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
